import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Check, Smartphone, Banknote, Wallet } from 'lucide-react';
import MarketingNav from '../components/MarketingNav';
import MarketingFooter from '../components/MarketingFooter';
import { useLocalization } from '../features/localization/LocalizationContext';
import {
  formatLocalAmount,
  formatLocalMoney,
} from '../features/localization/locales';

const reveal = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

const industries = [
  {
    id: 'salon',
    label: 'Salon',
    line: 'Braids, locs, silk press and colour — booked with the right stylist and the right amount of time.',
    image: '/images/spirara-industry-salon.jpg',
    alt: 'Stylist working on a client at a modern African hair salon',
  },
  {
    id: 'spa',
    label: 'Spa',
    line: 'Treatment rooms, therapists and add-ons that stay in sync all day long.',
    image: '/images/spirara-industry-spa.jpg',
    alt: 'Therapist preparing oils in a calm African day spa',
  },
  {
    id: 'barber',
    label: 'Barber',
    line: 'Walk-ins and regulars in one chair list, so nobody waits and no chair sits empty.',
    image: '/images/spirara-industry-barber.jpg',
    alt: 'Barber giving a haircut in a stylish African barbershop',
  },
  {
    id: 'nail',
    label: 'Nail',
    line: 'Sets, soak-offs and refills priced and timed properly before the client sits down.',
    image: '/images/spirara-industry-nails.jpg',
    alt: 'Nail technician painting a client\u2019s nails in a nail studio',
  },
  {
    id: 'massage',
    label: 'Massage',
    line: 'Back-to-back sessions with buffer time your body will actually thank you for.',
    image: '/images/spirara-industry-massage.jpg',
    alt: 'Massage therapist working with a client in a warm wellness studio',
  },
];

const BookingMock = ({ profile }) => (
  <div className="w-full bg-sand-50 p-6 shadow-[0_30px_60px_-30px_rgba(6,18,15,0.45)] ring-1 ring-ink-900/10">
    <p className="font-display text-xl text-ink-900">Book with {profile.bookingBusiness}</p>
    <p className="mt-1 text-xs uppercase tracking-[0.18em] text-ink-600/60">Thursday, 14 August</p>

    <div className="mt-5 space-y-2">
      {profile.bookingServices.map((service, index) => (
        <div
          key={service.name}
          className={`flex items-center justify-between px-3 py-2.5 text-sm ${
            index === 0 ? 'bg-ink-900 text-sand-50' : 'bg-white text-ink-800'
          }`}
        >
          <span>
            {service.name}
            <span className={`block text-[11px] ${index === 0 ? 'text-sand-300' : 'text-ink-600/60'}`}>
              {service.meta}
            </span>
          </span>
          <span className="font-body text-xs">{formatLocalMoney(service.amount, profile)}</span>
        </div>
      ))}
    </div>

    <p className="mt-5 text-[11px] uppercase tracking-[0.18em] text-ink-600/60">Available today</p>
    <div className="mt-2 grid grid-cols-3 gap-2">
      {['09:30', '11:00', '13:15', '15:00', '16:30', '18:00'].map((slot, index) => (
        <span
          key={slot}
          className={`px-2 py-2 text-center text-xs ${
            index === 2 ? 'bg-saffron-500 text-ink-900' : 'bg-white text-ink-700 ring-1 ring-ink-900/10'
          }`}
        >
          {slot}
        </span>
      ))}
    </div>
  </div>
);

const PaymentsMock = ({ profile }) => {
  const { total, lines } = profile.paymentExample;
  return (
    <div className="w-full bg-sand-50 p-6 shadow-[0_30px_60px_-30px_rgba(6,18,15,0.45)] ring-1 ring-ink-900/10">
      <p className="text-[11px] uppercase tracking-[0.18em] text-ink-600/60">Checkout</p>
      <p className="mt-2 font-display text-4xl text-ink-900">{formatLocalMoney(total, profile)}</p>

      <div className="mt-5 space-y-2 border-t border-ink-900/10 pt-4 text-sm text-ink-700">
        {lines.map((line) => (
          <div
            key={line.label}
            className={`flex justify-between ${line.amount < 0 ? 'text-ink-600/70' : ''}`}
          >
            <span>{line.label}</span>
            <span>
              {line.amount < 0 ? '-' : ''}
              {formatLocalAmount(Math.abs(line.amount), profile)}
            </span>
          </div>
        ))}
      </div>

      <div className="mt-5 space-y-2">
        {[
          { icon: Smartphone, label: profile.mobileMoneyLabel, active: true },
          { icon: Wallet, label: 'Card', active: false },
          { icon: Banknote, label: 'Cash', active: false },
        ].map(({ icon: Icon, label, active }) => (
          <div
            key={label}
            className={`flex items-center gap-3 px-3 py-2.5 text-sm ${
              active ? 'bg-ink-900 text-sand-50' : 'bg-white text-ink-700 ring-1 ring-ink-900/10'
            }`}
          >
            <Icon className="h-4 w-4" />
            {label}
            {active && <Check className="ml-auto h-4 w-4 text-saffron-400" />}
          </div>
        ))}
      </div>
    </div>
  );
};

const ScheduleMock = ({ profile }) => {
  const hours = ['09', '11', '13', '15'];
  const [a, b, c] = profile.staffNames;
  const columns = [
    {
      staff: a,
      blocks: [
        { top: 0, height: 70, label: 'Retwist', tone: 'ink' },
        { top: 112, height: 28, label: 'Trim', tone: 'plain' },
      ],
    },
    {
      staff: b,
      blocks: [
        { top: 14, height: 24, label: 'Fade', tone: 'plain' },
        { top: 84, height: 70, label: 'Colour', tone: 'saffron' },
      ],
    },
    {
      staff: c,
      blocks: [{ top: 28, height: 112, label: 'Braids', tone: 'ink' }],
    },
  ];

  const tones = {
    ink: 'bg-ink-900 text-sand-50',
    plain: 'bg-white text-ink-800 ring-1 ring-ink-900/10',
    saffron: 'bg-saffron-500 text-ink-900',
  };

  return (
    <div className="w-full bg-sand-50 p-6 shadow-[0_30px_60px_-30px_rgba(6,18,15,0.45)] ring-1 ring-ink-900/10">
      <div className="flex items-baseline justify-between">
        <p className="font-display text-xl text-ink-900">Thursday</p>
        <p className="text-[11px] uppercase tracking-[0.18em] text-ink-600/60">3 staff</p>
      </div>

      <div className="mt-5 flex gap-3">
        <div className="relative w-6 flex-none pt-6">
          <div className="relative h-56">
            {hours.map((hour, index) => (
              <span
                key={hour}
                className="absolute -translate-y-1/2 text-[10px] tabular-nums text-ink-600/50"
                style={{ top: `${index * 56}px` }}
              >
                {hour}
              </span>
            ))}
          </div>
        </div>

        <div className="grid flex-1 grid-cols-3 gap-2">
          {columns.map((column) => (
            <div key={column.staff}>
              <p className="mb-2 h-4 truncate text-[10px] uppercase tracking-[0.14em] text-ink-600/60">
                {column.staff}
              </p>
              <div className="relative h-56 bg-white/70">
                {hours.map((hour, index) => (
                  <span
                    key={hour}
                    className="absolute inset-x-0 border-t border-ink-900/[0.07]"
                    style={{ top: `${index * 56}px` }}
                  />
                ))}
                {column.blocks.map((block) => (
                  <div
                    key={block.label}
                    className={`absolute inset-x-0 overflow-hidden px-1.5 py-1 text-[10px] leading-none ${tones[block.tone]}`}
                    style={{ top: `${block.top}px`, height: `${block.height}px` }}
                  >
                    {block.label}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-5 flex items-center justify-between border-t border-ink-900/10 pt-3 text-xs text-ink-600/70">
        <span>Booked out</span>
        <span className="text-ink-900">86%</span>
      </div>
    </div>
  );
};

const buildPillars = (profile) => [
  {
    id: 'booking',
    eyebrow: 'Online booking',
    heading: 'Your clients book themselves, day or night.',
    body:
      'A booking link built for the phone in their hand. Real service durations, real availability, and a deposit taken up front so the chair never sits empty.',
    points: ['Share one link on WhatsApp or Instagram', 'Deposits and reminders that cut no-shows'],
    Visual: BookingMock,
  },
  {
    id: 'payments',
    eyebrow: 'Payments',
    heading: `Take ${profile.mobileMoneyLabel.toLowerCase()}, card and cash in one flow.`,
    body:
      'Charge the way your clients already pay. Deposits online, balance at checkout, tips for your team, and one clean total at close of day.',
    points: [
      `${profile.mobileMoneyLabel}, card and cash side by side`,
      'Day-end totals that match the till',
    ],
    Visual: PaymentsMock,
  },
  {
    id: 'manage',
    eyebrow: 'Business management',
    heading: 'Run the whole floor from one calendar.',
    body:
      'Staff schedules, client history, service pricing and repeat visits in one place — so you can see what is working before the month closes.',
    points: ['Staff, rooms and chairs in a single view', 'Client history that follows every visit'],
    Visual: ScheduleMock,
  },
];

const withLocalParam = (path, searchParams) => {
  const local = searchParams.get('local') || searchParams.get('locale') || searchParams.get('country');
  if (!local) return path;
  const next = new URLSearchParams();
  next.set('local', local);
  return `${path}?${next.toString()}`;
};

const MarketplaceHome = () => {
  const [activeIndustry, setActiveIndustry] = useState(industries[0].id);
  const industry = industries.find((item) => item.id === activeIndustry) || industries[0];
  const { profile } = useLocalization();
  const [searchParams] = useSearchParams();
  const pillars = buildPillars(profile);
  const testimonials = profile.testimonials || [];

  return (
    <div className="min-h-screen bg-sand-100 font-body text-ink-900">
      {/* Hero */}
      <section className="relative isolate flex min-h-[92vh] items-end overflow-hidden bg-ink-900">
        <div className="absolute inset-0">
          <img
            src="/images/spirara-hero-salon.jpg"
            alt={`Stylist braiding a client's hair in a modern ${profile.demonym} salon`}
            className="h-full w-full origin-center object-cover object-[68%_center] animate-kenburns sm:object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-ink-950/90 via-ink-950/25 to-ink-950/40" />
          <div className="absolute inset-0 bg-gradient-to-r from-ink-950/80 via-ink-950/25 to-transparent" />
        </div>

        {/* Page nav */}
        <MarketingNav variant="dark" />

        <div className="relative mx-auto w-full max-w-7xl px-5 pb-16 pt-28 sm:px-6 sm:pb-24 sm:pt-32 lg:px-8 lg:pb-28">
          <div className="max-w-3xl">
            <motion.h1
              initial={{ opacity: 0, y: 32 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="font-display text-[clamp(3.75rem,13vw,9.5rem)] font-light leading-[0.82] tracking-[-0.035em] text-sand-50"
            >
              Spirara<span className="text-saffron-500">.</span>
            </motion.h1>

            <motion.p
              key={profile.countryCode}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.14, ease: [0.22, 1, 0.36, 1] }}
              className="mt-6 max-w-2xl font-display text-[clamp(1.5rem,3.6vw,2.6rem)] font-light leading-[1.15] text-sand-100 sm:mt-8"
            >
              {profile.heroRegion
                ? `All-in-one booking, payments & business management for ${profile.heroRegion}`
                : 'All-in-one booking, payments & business management solution for Africa'}
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.26, ease: [0.22, 1, 0.36, 1] }}
              className="mt-5 max-w-xl text-base leading-relaxed text-sand-200/90 sm:text-lg"
            >
              {profile.city
                ? `Everything you need to run and grow your business in ${profile.city} and beyond. Trusted by beauty businesses, loved by their clients.`
                : 'Everything you need to run and grow your business. Trusted by beauty businesses, loved by their clients.'}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.38, ease: [0.22, 1, 0.36, 1] }}
              className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4"
            >
              <Link
                to="/book/new-spa-tema"
                className="group inline-flex items-center justify-center gap-2 bg-saffron-500 px-7 py-4 text-sm font-semibold uppercase tracking-[0.12em] text-ink-900 transition-colors duration-300 hover:bg-saffron-400"
              >
                Book Now
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to={withLocalParam('/signup', searchParams)}
                className="inline-flex items-center justify-center px-7 py-4 text-sm font-semibold uppercase tracking-[0.12em] text-sand-100 ring-1 ring-inset ring-sand-100/40 transition-colors duration-300 hover:bg-sand-50/10"
              >
                Sign up
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Built for */}
      <section className="bg-sand-100 pt-20 sm:pt-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
          <motion.h2 {...reveal} className="max-w-xl font-display text-4xl font-light leading-tight text-ink-900 sm:max-w-none sm:text-5xl">
            Built for the businesses
            <br className="hidden sm:block" /> people come back for.
          </motion.h2>

          <motion.div {...reveal} className="mt-8 flex flex-wrap gap-x-6 gap-y-2 sm:mt-10 sm:gap-x-10">
            {industries.map((item) => {
              const isActive = item.id === activeIndustry;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setActiveIndustry(item.id)}
                  className={`relative pb-2 font-display text-xl transition-colors duration-300 sm:text-3xl ${
                    isActive ? 'text-ink-900' : 'text-ink-900/35 hover:text-ink-900/70'
                  }`}
                >
                  {item.label}
                  {isActive && (
                    <motion.span
                      layoutId="industry-underline"
                      className="absolute inset-x-0 bottom-0 h-[3px] bg-saffron-500"
                    />
                  )}
                </button>
              );
            })}
          </motion.div>

          <div className="mt-8 min-h-[6.5rem] sm:min-h-[5.5rem]">
            <AnimatePresence mode="wait">
              <motion.p
                key={industry.id}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                className="max-w-2xl font-display text-xl font-light leading-snug text-ink-800/90 sm:text-2xl"
              >
                {industry.line}
              </motion.p>
            </AnimatePresence>
          </div>
        </div>

        <div className="relative mt-6 h-[52vh] min-h-[320px] w-full overflow-hidden bg-ink-800 sm:mt-10 sm:h-[68vh]">
          <AnimatePresence mode="wait">
            <motion.img
              key={industry.id}
              src={industry.image}
              alt={industry.alt}
              initial={{ opacity: 0, scale: 1.06 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="absolute inset-0 h-full w-full object-cover"
            />
          </AnimatePresence>
        </div>
      </section>

      {/* Product pillars */}
      {pillars.map((pillar, index) => {
        const isDark = index % 2 === 1;
        const Visual = pillar.Visual;

        return (
          <section
            key={pillar.id}
            className={`py-20 sm:py-28 ${isDark ? 'bg-ink-900 text-sand-100' : 'bg-sand-50 text-ink-900'}`}
          >
            <div className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
              <div
                className={`grid items-center gap-12 lg:grid-cols-2 lg:gap-20 ${
                  index % 2 === 1 ? 'lg:[&>*:first-child]:order-2' : ''
                }`}
              >
                <motion.div {...reveal}>
                  <p
                    className={`text-xs uppercase tracking-[0.22em] ${
                      isDark ? 'text-saffron-400' : 'text-saffron-600'
                    }`}
                  >
                    {pillar.eyebrow}
                  </p>
                  <h2 className="mt-4 font-display text-3xl font-light leading-[1.12] sm:text-4xl lg:text-5xl">
                    {pillar.heading}
                  </h2>
                  <p
                    className={`mt-5 max-w-lg text-base leading-relaxed sm:text-lg ${
                      isDark ? 'text-sand-200/85' : 'text-ink-700/80'
                    }`}
                  >
                    {pillar.body}
                  </p>
                  <ul className="mt-7 space-y-3">
                    {pillar.points.map((point) => (
                      <li key={point} className="flex items-start gap-3 text-sm sm:text-base">
                        <Check
                          className={`mt-0.5 h-5 w-5 flex-none ${
                            isDark ? 'text-saffron-400' : 'text-saffron-600'
                          }`}
                        />
                        <span className={isDark ? 'text-sand-200/90' : 'text-ink-700/85'}>{point}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 36 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
                  className="relative mx-auto w-full max-w-[420px]"
                >
                  <span
                    aria-hidden="true"
                    className={`absolute -bottom-5 -right-5 left-10 top-12 hidden border sm:block ${
                      isDark ? 'border-saffron-500/45' : 'border-ink-900/15'
                    }`}
                  />
                  <div className="relative">
                    <Visual profile={profile} />
                  </div>
                </motion.div>
              </div>
            </div>
          </section>
        );
      })}

      {/* Proof */}
      <section className="bg-sand-100 py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
          <motion.h2
            {...reveal}
            className="max-w-2xl font-display text-4xl font-light leading-tight text-ink-900 sm:text-5xl"
          >
            Trusted by beauty businesses, loved by their clients.
          </motion.h2>

          <div className="mt-14 grid gap-12 md:grid-cols-3 md:gap-10">
            {testimonials.map((item, index) => (
              <motion.blockquote
                key={`${profile.countryCode}-${item.name}`}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.7, delay: index * 0.12, ease: [0.22, 1, 0.36, 1] }}
              >
                <p className="font-display text-6xl leading-none text-saffron-500">&ldquo;</p>
                <p className="mt-3 font-display text-xl font-light leading-snug text-ink-800 sm:text-2xl">
                  {item.quote}
                </p>
                <footer className="mt-6 text-sm text-ink-700/70">
                  <span className="block font-semibold text-ink-900">{item.name}</span>
                  {item.role}, {item.business}
                </footer>
              </motion.blockquote>
            ))}
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="bg-ink-900 py-24 sm:py-32">
        <div className="mx-auto max-w-4xl px-5 text-center sm:px-6">
          <motion.h2
            {...reveal}
            className="font-display text-4xl font-light leading-[1.1] text-sand-50 sm:text-6xl"
          >
            Start taking bookings
            <br />
            this week.
          </motion.h2>
          <motion.div {...reveal} className="mt-10 flex justify-center">
            <Link
              to={withLocalParam('/signup', searchParams)}
              className="group inline-flex items-center gap-2 bg-saffron-500 px-8 py-4 text-sm font-semibold uppercase tracking-[0.12em] text-ink-900 transition-colors duration-300 hover:bg-saffron-400"
            >
              Sign up
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </motion.div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
};

export default MarketplaceHome;
